/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useMessages } from "@epic-mw/i18n";
import { Fragment, PropsWithChildren, useState } from "react";

import { CosmosText, CosmosTextVariantEnum, EdsIcon } from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";
import { SystemStyleObject } from "@panda/types";

const Viewport3CardsControlCss: SystemStyleObject = {
	smDown: {
		"&[data-show-more=true] > *:not(.viewport-control):nth-child(n+4)": { display: "none" },
	},
};
const Viewport2CardsControlCss: SystemStyleObject = {
	smDown: {
		"&[data-show-more=true] > *:not(.viewport-control):nth-child(n+3)": { display: "none" },
	},
};

export type CollapsibleCardViewportProps = PropsWithChildren<{
	viewportCss?: SystemStyleObject;
	nVisibleChild?: 2 | 3;
	enabled?: boolean;
}>;

export const CollapsibleCardViewport = ({
	viewportCss,
	nVisibleChild = 3,
	enabled = true,
	children,
}: CollapsibleCardViewportProps): React.JSX.Element => {
	const [showMore, setShowMore] = useState(true);

	const messages = useMessages();

	return (
		<div
			className={cx(
				"collapsible-card-viewport",
				css(viewportCss, nVisibleChild === 2 ? Viewport2CardsControlCss : Viewport3CardsControlCss),
			)}
			data-show-more={showMore}
		>
			{children}
			{!enabled ? undefined : (
				<Fragment>
					<HStack
						gap="eds.dimension.4"
						className={cx(
							"viewport-control",
							css({
								flexBasis: "100%",
								color: "eds.text.link.primary.default",
								transition: `color 0.2s token(animations.eds.easing.easeInOutQuad)`,
								_hover: {
									color: "eds.text.link.primary.press",
									cursor: "pointer",
								},
								"&&": {
									sm: {
										display: "none",
									},
								},
							}),
						)}
						onClick={() => setShowMore((showMore) => !showMore)}
					>
						<CosmosText variant={CosmosTextVariantEnum.paragraphMD}>
							{showMore
								? messages["epic.cosmos.showmore"] || "Show more"
								: messages["epic.cosmos.showless"] || "Show less"}
						</CosmosText>
						<EdsIcon icon={showMore ? "ChevronDown" : "ChevronUp"} />
					</HStack>
				</Fragment>
			)}
		</div>
	);
};
